import { ComponentClass, FunctionComponent } from 'react';

import { withFormik } from 'formik';
import compose from 'lodash/fp/compose';

import { withHigherOrderComponent } from '../../../../../../shared/components/with-higher-order-component/with-higher-order-component';
import { useNoSubmit } from '../../../../../../shared/hooks/use-no-submit/use-no-submit.hook';
import { BasicInfoFormikModel } from '../../models/basic-info-formik.model';
import { getBasicInfoFormValidationSchema } from '../../schema/get-basic-info-form-validation.schema';
import { OuterPropsAndForceErrorsDisplay } from '../../types/outer-props-and-force-errors-display.type';
import { ActionButtons } from '../action-buttons/action-buttons';
import { AllowDownloadingCheckboxInput } from '../allow-downloading-checkbox-input/allow-downloading-checkbox-input';
import { BasicInformationFormContainer } from '../basic-information-form-container/basic-information-form-container';
import { CampaignDescriptionInput } from '../campaign-description-input/campaign-description-input';
import CampaignFirmwaresSelect from '../campaign-firmwares-select/campaign-firmwares-select';
import { CampaignNameInput } from '../campaign-name-input/campaign-name-input';
import { CampaignStatusSelect } from '../campaign-status-select/campaign-status-select';
import { DateRangeInput } from '../date-range-input/date-range-input';
import { FotaUpdateTypeEnum } from '../fota-update-type-radio-group-input/enum/fota-update-type.enum';
import { FotaUpdateTypeRadioGroupInput } from '../fota-update-type-radio-group-input/fota-update-type-radio-group-input';
import { MaxRetryNoInput } from '../max-retry-no-input/max-retry-no-input';
import { SafetyRelevantCheckboxInput } from '../safety-relevant-checkbox-input/safety-relevant-checkbox-input';
import { ServerInitiatedTypeEnum } from '../server-initiated-type-radio-group-input/enum/server-intiated-type.enum';
import { ServerInitiatedTypeRadioGroupInput } from '../server-initiated-type-radio-group-input/server-initiated-type-radio-group-input';
import { VersionInput } from '../version-input/version-input';
import { WifiExpirationDateInput } from '../wifi-expiration-date-input/wifi-expiration-date-input';

const getBasicInfoFormLayout = ():
  | ComponentClass<OuterPropsAndForceErrorsDisplay>
  | FunctionComponent<OuterPropsAndForceErrorsDisplay> =>
  withFormik<OuterPropsAndForceErrorsDisplay, BasicInfoFormikModel>({
    mapPropsToValues: props => ({
      name: props.basicInfoStateFields?.name || '',
      startDate: props.basicInfoStateFields?.startDate ?? null,
      endDate: props.basicInfoStateFields?.endDate ?? null,
      wifiExpirationDate:
        props.basicInfoStateFields?.wifiExpirationDate ?? null,
      fotaUpdateType:
        props.basicInfoStateFields?.fotaUpdateType ||
        FotaUpdateTypeEnum.REGULAR,
      serverInitiatedType:
        props.basicInfoStateFields?.serverInitiatedType ||
        ServerInitiatedTypeEnum.NONE,
      safetyRelevant: props.basicInfoStateFields?.safetyRelevant || true,
      maxRetryNo: props.basicInfoStateFields?.maxRetryNo ?? undefined,
      is5GDownloadingAllowed:
        props.basicInfoStateFields?.is5GDownloadingAllowed || true,
      description: props.basicInfoStateFields?.description || '',
      status: props.basicInfoStateFields?.status ?? null,
      firmwareIds: props.basicInfoStateFields?.firmwareIds || [],
      version: props.basicInfoStateFields?.version || '',
    }),
    validationSchema: getBasicInfoFormValidationSchema,
    validateOnMount: false,
    handleSubmit: useNoSubmit(),
    validateOnChange: true,
    validateOnBlur: false,
  })(
    compose(
      withHigherOrderComponent(CampaignNameInput),
      withHigherOrderComponent(DateRangeInput),
      withHigherOrderComponent(WifiExpirationDateInput),
      withHigherOrderComponent(FotaUpdateTypeRadioGroupInput),
      withHigherOrderComponent(ServerInitiatedTypeRadioGroupInput),
      withHigherOrderComponent(SafetyRelevantCheckboxInput),
      withHigherOrderComponent(MaxRetryNoInput),
      withHigherOrderComponent(AllowDownloadingCheckboxInput),
      withHigherOrderComponent(VersionInput),
      withHigherOrderComponent(CampaignDescriptionInput),
      withHigherOrderComponent(CampaignFirmwaresSelect),
      withHigherOrderComponent(CampaignStatusSelect),
      withHigherOrderComponent(ActionButtons)
    )(BasicInformationFormContainer)
  );

export default getBasicInfoFormLayout;
